import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { PublicCreativeUnitPackageListing } from '../../../../../../../api/src/creative-unit-package-listing/creative-unit-package-listing.entity';
import { sortArrayByAlpha } from '../../../utils/array.utils';
import { GlobalQuery } from "../../global";
import { GlobalSettings } from "../../global/global.model";
import { Observable } from "rxjs";
import { LocaleDto } from '../../../../../../../api/src/creative-unit-package/models/locale.dto';
import { ExportType } from '../../../../../../../api/src/encode/models/job-def.dto';

@Component({
	selector: 'app-creative-unit-package-listing-card',
	templateUrl: './creative-unit-package-listing-card.component.html',
	styleUrls: ['./creative-unit-package-listing-card.component.scss']
})
export class CreativeUnitPackageListingCardComponent implements OnChanges {
	@Input() listing: PublicCreativeUnitPackageListing;
	@Input() parentListing: PublicCreativeUnitPackageListing; // If this is a child listing

	@Input() highlightQuery: string = ''; // Highlight a regex match in the group/item names

	public readonly ExportType = ExportType;

	settings$: Observable<GlobalSettings>;

	public visibleCreativeUnitsNames: { name: string, locale: string | LocaleDto }[];
	public exportTypes: ExportType[];

	constructor(private globalQuery: GlobalQuery) {
		this.settings$ = this.globalQuery.select('settings')
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.setVisibleCreativeUnitsNames();
		this.setExportTypes();
	}

	setExportTypes() {
		let exportTypes = this.listing.creativeUnitPackageSummary?.exportTypes || this.listing['exportTypes'] || [];

		// If there are child listings, merge their export types into the parent listing's export types.
		if (this.listing.children && this.listing.children.length > 0) {
			for (const child of this.listing.children) {
				if (child.creativeUnitPackageSummary && child.creativeUnitPackageSummary.exportTypes) {
					exportTypes = [
						...(exportTypes || []),
						...(child.creativeUnitPackageSummary.exportTypes || [])
					];
				}
			}
		}

		// Sort by alpha and remove unique values to ensure no duplicates in the export types.
		if (exportTypes && Array.isArray(exportTypes)) {
			exportTypes = sortArrayByAlpha(exportTypes).filter((value, index, self) => {
				// Filter out empty values and duplicates.
				return self.findIndex(v => v === value) === index;
			});
		} else {
			exportTypes = [];
		}

		this.exportTypes = exportTypes;
	}

	setVisibleCreativeUnitsNames() {
		let visibleCreativeUnitsNames = this.listing.creativeUnitPackageSummary?.visibleCreativeUnitsNames || this.listing['visibleCreativeUnitsNames'] || [];

		if (this.listing.children && this.listing.children.length > 0) {
			// If there are child listings, merge their visible creative units into the parent listing's visible creative units.
			for (const child of this.listing.children) {
				if (child.creativeUnitPackageSummary && child.creativeUnitPackageSummary.visibleCreativeUnitsNames) {
					// Merge the arrays, avoiding duplicates.
					visibleCreativeUnitsNames = [
						...visibleCreativeUnitsNames,
						...child.creativeUnitPackageSummary.visibleCreativeUnitsNames
					];
				}
			}
		}

		// Filter our any units with the exact same name to avoid duplicates in the display.
		this.visibleCreativeUnitsNames = visibleCreativeUnitsNames.filter((value, index, self) => {
			if (!value || !value.name) {
				return false; // filter out empty values
			}
			// Find the first occurrence of the name in the array.
			const firstIndex = self.findIndex(v => v.name === value.name);
			// Only keep the first occurrence.
			return firstIndex === index;
		}
		).sort((a, b) => {
			// Sort by name alphabetically.
			if (!a.name || !b.name) {
				return 0; // fallback to default if name is missing
			}
			const nameA = a.name.toLowerCase();
			const nameB = b.name.toLowerCase();

			if (nameA < nameB) {
				return -1;
			}
			if (nameA > nameB) {
				return 1;
			}
			return 0;
		});
	}

	sortArrayByAlpha(array: any[]): any[] {
		if (!array) {
			return [];
		}
		return sortArrayByAlpha(array);
	}

}
